"use client";

import * as React from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import type { LatestAffiliateSale } from "@/services/brand.service";
import type { StatValue } from "@/types/dashboard";
import { formatAmount } from "@/utils/formatters";
import { DashboardCard } from "@/components/dashboard/dashboard-card";

const columns: ColumnDef<LatestAffiliateSale>[] = [
  {
    accessorKey: "orderId",
    header: "Order",
    meta: { width: "w-40" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
        {row.original.orderId}
      </span>
    ),
  },
  {
    accessorKey: "code",
    header: "Code",
    meta: { width: "w-32" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-foreground">{row.original.code}</span>
    ),
  },
  {
    accessorKey: "orderTotal",
    header: "Order total",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatAmount(parseFloat(row.original.orderTotal))}
      </span>
    ),
  },
  {
    accessorKey: "affliateCommission",
    header: "Commission",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatAmount(
          Math.round(parseFloat(row.original.affliateCommission) * 100) / 100,
        )}
      </span>
    ),
  },
  {
    accessorKey: "date",
    header: "Date",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {new Date(row.original.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })}
      </span>
    ),
  },
];

// Dashboard widget: always shows the latest 20 sales, no pagination.
const MAX_ROWS = 20;

/** Always matches the network card's height beside it (see the dashboard
    grid) and scrolls inside; an empty list centres its message in that space. */
export function TopAffiliateTable({
  data,
  stats,
}: {
  data: LatestAffiliateSale[];
  stats?: StatValue[];
}) {
  const rows = React.useMemo(() => data.slice(0, MAX_ROWS), [data]);

  return (
    <DashboardCard
      id="dashboard-top-affiliate-table"
      title="Latest sales"
      fill
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      {rows.length === 0 ? (
        <div className="flex min-h-[10rem] flex-1 items-center justify-center text-sm text-muted-foreground lg:min-h-0">
          No sales yet
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          emptyText="No sales yet"
          maxHeight="max-h-[30rem] lg:max-h-full"
          className="lg:h-full"
        />
      )}
    </DashboardCard>
  );
}
